import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchSupplierSourceProfiles,
  previewSupplierSourceProfile,
  updateSupplierSourceProfile,
  type SupplierSourceExpectation,
  type SupplierSourceKind,
  type SupplierSourceProfile,
  type SupplierSourceProfileCheck,
} from "../../api/sourceProfiles";
import FilterDropdown from "../../components/FilterDropdown";
import {
  SourceBadge,
  sourceBadgeKindFromSourceKind,
} from "../../components/SourceBadge";
import { ApiError } from "../../lib/api";
import { formatDateShort } from "../../lib/format";
import { useEscapeKey } from "../../lib/useEscapeKey";

const SOURCE_KINDS: readonly SupplierSourceKind[] = [
  "pdf",
  "ugl",
  "datanorm",
];

const EXPECTATION_OPTIONS: ReadonlyArray<{
  value: SupplierSourceExpectation;
  label: string;
}> = [
  { value: "required", label: "Regelmäßig erwartet" },
  { value: "optional", label: "Falls vorhanden" },
  { value: "absent", label: "Wird nicht geliefert" },
];

const DRAWER_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function SupplierSourceProfilePanel({
  canManageProfiles,
}: {
  canManageProfiles: boolean;
}) {
  const queryClient = useQueryClient();
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [editingSupplier, setEditingSupplier] = useState<string | null>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const profilesQuery = useQuery({
    queryKey: ["source-coverage", "profiles"],
    queryFn: fetchSupplierSourceProfiles,
    staleTime: 30_000,
  });
  const profiles = profilesQuery.data?.profiles ?? [];
  const selectedProfile =
    profiles.find((profile) => profile.supplier === selectedSupplier) ??
    profiles[0] ??
    null;
  const editingProfile =
    profiles.find((profile) => profile.supplier === editingSupplier) ?? null;
  const supplierOptions = profiles.map((profile) => ({
    value: profile.supplier,
    label: profile.supplier,
  }));
  const updateMutation = useMutation({
    mutationFn: updateSupplierSourceProfile,
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(
        ["source-coverage", "profiles"],
        (
          current:
            | Awaited<ReturnType<typeof fetchSupplierSourceProfiles>>
            | undefined,
        ) =>
          current
            ? {
                ...current,
                profiles: current.profiles.map((profile) =>
                  profile.supplier === updatedProfile.supplier
                    ? updatedProfile
                    : profile,
                ),
              }
            : current,
      );
      setSelectedSupplier(updatedProfile.supplier);
      setEditingSupplier(null);
      void queryClient.invalidateQueries({
        queryKey: ["imports", "quality", updatedProfile.supplier],
      });
      void queryClient.invalidateQueries({
        queryKey: ["imports", "quality-review", updatedProfile.supplier],
      });
      void queryClient.invalidateQueries({ queryKey: ["source-coverage"] });
      window.requestAnimationFrame(() => editButtonRef.current?.focus());
    },
  });

  if (profilesQuery.isLoading) {
    return (
      <section
        aria-label="Datenquellen nach Großhändler"
        className="mb-5 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
      >
        <p className="text-sm text-slate-500">Datenquellen werden geladen …</p>
      </section>
    );
  }

  if (profilesQuery.isError || !selectedProfile) {
    return (
      <section
        aria-label="Datenquellen nach Großhändler"
        className="mb-5 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
      >
        <p className="text-sm font-medium text-slate-800">
          Datenquellen nicht verfügbar
        </p>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          Welche Quellen erwartet und welche Prüfungen aktiv sind, konnte nicht
          geladen werden.
        </p>
      </section>
    );
  }

  const activeCheckCount = selectedProfile.checks.filter(
    (check) => check.status === "active",
  ).length;

  return (
    <>
      <section
        aria-label="Datenquellen nach Großhändler"
        className="mb-5 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="mr-auto min-w-[12rem]">
            <h2 className="text-sm font-semibold text-slate-900">
              Datenquellen für {selectedProfile.supplier}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Welche Dateien erhältst du von diesem Großhändler?
            </p>
          </div>
          <FilterDropdown
            label="Großhändler"
            options={supplierOptions}
            value={selectedProfile.supplier}
            defaultValue={selectedProfile.supplier}
            showValue
            onChange={(supplier) => {
              updateMutation.reset();
              setSelectedSupplier(supplier);
            }}
            ariaLabel="Großhändler für Datenquellen auswählen"
          />
          <span className="text-sm font-medium tabular-nums text-slate-700">
            {activeCheckCount === 1
              ? "1 Prüfart aktiv"
              : `${activeCheckCount.toLocaleString("de-AT")} Prüfarten aktiv`}
          </span>
          {canManageProfiles ? (
            <button
              ref={editButtonRef}
              type="button"
              onClick={() => {
                updateMutation.reset();
                setEditingSupplier(selectedProfile.supplier);
              }}
              className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Datenquellen bearbeiten
            </button>
          ) : null}
        </div>

        <section className="mt-4 border-t border-slate-100 pt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Erwartete Dateien
              </h3>
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {selectedProfile.sources.map((source) => (
                  <li
                    key={source.source}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <SourceBadge
                      kind={sourceBadgeKindFromSourceKind(source.source)}
                      label={sourceLabel(source.source)}
                      inactive={
                        source.expectation === "absent" || !source.supported
                      }
                    />
                    <span className="text-right text-xs text-slate-600">
                      {source.supported
                        ? `${expectationLabel(source.expectation)}${
                            source.observed
                              ? " · vorhanden"
                              : source.expectation === "absent"
                                ? ""
                                : " · noch nicht vorhanden"
                          }`
                        : "Von TruuCost nicht unterstützt"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Damit mögliche Prüfungen
              </h3>
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {selectedProfile.checks.map((check) => (
                  <li key={check.kind} className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {checkLabel(check.kind)}
                      </span>
                      <CheckStatusBadge check={check} />
                    </div>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {check.reason}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {selectedProfile.provenance !== "configured" ? (
            <p className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
              {selectedProfile.provenance === "unconfigured"
                ? "Die erwarteten Datenquellen wurden noch nicht bestätigt."
                : "Aktuell gelten die voreingestellten Datenquellen. Du kannst sie an die Dateien anpassen, die du erhältst."}
            </p>
          ) : null}
          {selectedProfile.reason ? (
            <p className="mt-3 text-xs leading-5 text-slate-500">
              Letzte Begründung: {selectedProfile.reason}
            </p>
          ) : null}
          {selectedProfile.updated_at ? (
            <p className="mt-1 text-xs text-slate-400">
              Aktualisiert am {formatDateShort(selectedProfile.updated_at)}
            </p>
          ) : null}
        </section>
      </section>

      <SupplierSourceProfileDrawer
        profile={editingProfile}
        pending={updateMutation.isPending}
        error={updateMutation.error}
        onClose={() => {
          updateMutation.reset();
          setEditingSupplier(null);
          window.requestAnimationFrame(() => editButtonRef.current?.focus());
        }}
        onSave={({ expectations, reason }) =>
          updateMutation.mutate({
            supplier: editingProfile?.supplier ?? selectedProfile.supplier,
            expectedRevision:
              editingProfile?.revision ?? selectedProfile.revision,
            requestId: createRequestId(),
            expectations,
            reason,
          })
        }
      />
    </>
  );
}

function SupplierSourceProfileDrawer({
  profile,
  pending,
  error,
  onClose,
  onSave,
}: {
  profile: SupplierSourceProfile | null;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSave: (input: {
    expectations: Record<SupplierSourceKind, SupplierSourceExpectation>;
    reason: string;
  }) => void;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [expectations, setExpectations] = useState<
    Record<SupplierSourceKind, SupplierSourceExpectation>
  >({
    pdf: "absent",
    ugl: "absent",
    datanorm: "absent",
  });
  const [reason, setReason] = useState("");
  const [loadedRevision, setLoadedRevision] = useState<string | null>(null);
  const open = Boolean(profile);
  const previewQuery = useQuery({
    queryKey: [
      "source-coverage",
      "profiles",
      "preview",
      profile?.supplier,
      expectations.pdf,
      expectations.ugl,
      expectations.datanorm,
    ],
    queryFn: () =>
      previewSupplierSourceProfile({
        supplier: profile?.supplier ?? "",
        expectations,
      }),
    enabled: open && loadedRevision === profile?.revision,
    retry: false,
  });

  useEscapeKey(onClose, open);
  useEffect(() => {
    if (!profile) return;
    setExpectations(expectationsFromProfile(profile));
    setReason("");
    setLoadedRevision(profile.revision);
  }, [profile]);
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const frame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus(),
    );
    return () => {
      window.cancelAnimationFrame(frame);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !profile) return null;
  const initialExpectations = expectationsFromProfile(profile);
  const changed =
    profile.provenance !== "configured" ||
    SOURCE_KINDS.some(
      (source) => expectations[source] !== initialExpectations[source],
    );
  const saveDisabled =
    pending ||
    previewQuery.isPending ||
    previewQuery.isError ||
    !changed ||
    reason.trim().length < 3;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="Datenquellen schließen"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="source-profile-drawer-title"
        onKeyDown={trapDrawerFocus}
        className="absolute inset-y-0 right-0 flex w-[min(34rem,100vw)] max-w-full flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Mögliche Prüfungen
            </div>
            <h2
              id="source-profile-drawer-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              Datenquellen für {profile.supplier}
            </h2>
            <p className="mt-1 text-sm leading-5 text-slate-500">
              Geben Sie an, welche Quellen Sie von diesem Großhändler erhalten.
              TruuCost leitet daraus die anwendbaren Prüfungen ab.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="h-8 shrink-0 rounded-md px-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            Schließen
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-3">
            {SOURCE_KINDS.map((sourceKind) => (
              <div
                key={sourceKind}
                className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,auto)] sm:items-center"
              >
                <div>
                  <SourceBadge
                    kind={sourceBadgeKindFromSourceKind(sourceKind)}
                    label={sourceLabel(sourceKind)}
                    inactive={
                      expectations[sourceKind] === "absent" ||
                      profile.sources.find(
                        (source) => source.source === sourceKind,
                      )?.supported === false
                    }
                  />
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {profile.sources.find(
                      (source) => source.source === sourceKind,
                    )?.supported === false
                      ? "Diese Quelle kann für diesen Großhändler derzeit nicht verarbeitet werden."
                      : sourceExpectationHint(expectations[sourceKind])}
                  </p>
                </div>
                <FilterDropdown<SupplierSourceExpectation>
                  label={`${sourceLabel(sourceKind)}-Erwartung`}
                  options={EXPECTATION_OPTIONS}
                  value={expectations[sourceKind]}
                  defaultValue={initialExpectations[sourceKind]}
                  disabled={
                    profile.sources.find(
                      (source) => source.source === sourceKind,
                    )?.supported === false
                  }
                  onChange={(expectation) =>
                    setExpectations((current) => ({
                      ...current,
                      [sourceKind]: expectation,
                    }))
                  }
                  ariaLabel={`${sourceLabel(sourceKind)}-Erwartung`}
                  layout="field"
                />
              </div>
            ))}
          </div>

          <section
            aria-labelledby="source-profile-preview-title"
            className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3"
          >
            <h3
              id="source-profile-preview-title"
              className="text-sm font-semibold text-slate-900"
            >
              Auswirkung auf Prüfungen
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Diese Vorschau wird aus den gewählten Quellen abgeleitet und noch
              nicht gespeichert.
            </p>
            {previewQuery.isLoading ? (
              <p className="mt-3 text-xs text-slate-500">
                Mögliche Prüfungen werden berechnet …
              </p>
            ) : previewQuery.isError || !previewQuery.data ? (
              <p className="mt-3 text-xs text-rose-700">
                Die Vorschau konnte nicht geladen werden.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
                {previewQuery.data.checks.map((check) => (
                  <li key={check.kind} className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {checkLabel(check.kind)}
                      </span>
                      <CheckStatusBadge check={check} />
                    </div>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {check.reason}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <label className="mt-5 block text-sm font-medium text-slate-800">
            Grund der Änderung *
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={4}
              minLength={3}
              maxLength={1000}
              placeholder="Warum ändern sich die erwarteten Datenquellen?"
              className="mt-1 block w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </label>

          {error ? (
            <p
              role="alert"
              className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              {profileSaveError(error)}
            </p>
          ) : null}
        </div>

        <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={saveDisabled}
            onClick={() =>
              onSave({ expectations, reason: reason.trim() })
            }
            className="inline-flex h-9 items-center rounded-md bg-emerald-600 px-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending
              ? "Speichert …"
              : profile.provenance === "configured"
                ? "Datenquellen speichern"
                : "Datenquellen bestätigen"}
          </button>
        </footer>
      </aside>
    </div>
  );
}

function CheckStatusBadge({
  check,
}: {
  check: SupplierSourceProfileCheck;
}) {
  const config =
    check.status === "active"
      ? {
          label: "Aktiv",
          className: "bg-emerald-50 text-emerald-800",
        }
      : check.status === "waiting_for_source"
        ? {
            label: "Wartet auf Quelle",
            className: "bg-amber-50 text-amber-800",
          }
        : {
            label: "Nicht anwendbar",
            className: "bg-slate-100 text-slate-600",
          };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function expectationsFromProfile(
  profile: SupplierSourceProfile,
): Record<SupplierSourceKind, SupplierSourceExpectation> {
  return {
    pdf: editableExpectation(profile, "pdf"),
    ugl: editableExpectation(profile, "ugl"),
    datanorm: editableExpectation(profile, "datanorm"),
  };
}

function editableExpectation(
  profile: SupplierSourceProfile,
  sourceKind: SupplierSourceKind,
): SupplierSourceExpectation {
  const source = profile.sources.find(
    (candidate) => candidate.source === sourceKind,
  );
  if (!source?.supported) return "absent";
  return source.expectation;
}

function sourceLabel(source: SupplierSourceKind): string {
  if (source === "pdf") return "PDF";
  if (source === "ugl") return "UGL";
  return "DATANORM";
}

function expectationLabel(
  expectation: SupplierSourceExpectation | undefined,
): string {
  return (
    EXPECTATION_OPTIONS.find((option) => option.value === expectation)?.label ??
    "Noch nicht festgelegt"
  );
}

function sourceExpectationHint(
  expectation: SupplierSourceExpectation,
): string {
  if (expectation === "required") {
    return "Fehlt diese Quelle, wartet die zugehörige Prüfung auf Nachweise.";
  }
  if (expectation === "optional") {
    return "Die Quelle wird genutzt, sobald sie vorliegt; ihr Fehlen ist kein Fehler.";
  }
  return "Prüfungen, die diese Quelle benötigen, sind nicht anwendbar.";
}

function checkLabel(
  kind: SupplierSourceProfileCheck["kind"],
): string {
  if (kind === "parse_validation_line") return "Preiszeilen";
  if (kind === "reconciliation_supplier_case") return "Belegabgleich";
  return "Artikelsets";
}

function profileSaveError(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return "Die Datenquellen wurden inzwischen geändert. Bitte schließen Sie den Dialog, laden Sie die Seite neu und versuchen Sie es erneut.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return "Für diese Änderung fehlt Ihnen der Rollenbereich Administrator.";
  }
  return "Die Datenquellen konnten nicht gespeichert werden.";
}

function createRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

function trapDrawerFocus(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "Tab") return;
  const focusable = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      DRAWER_FOCUSABLE_SELECTOR,
    ),
  ).filter((element) => element.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (
    event.shiftKey &&
    (active === first || !event.currentTarget.contains(active))
  ) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (
    !event.shiftKey &&
    (active === last || !event.currentTarget.contains(active))
  ) {
    event.preventDefault();
    first.focus();
  }
}
